import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { Button } from "./ui/Button";
import { Overlay } from "./ui/Overlay";
import { cn } from "./ui/cn";
import type { SavedView, User } from "../types";

/**
 * Create or edit a saved view.
 *
 * The filters are stored server-side and only the id travels with a request,
 * so editing one takes effect everywhere at once and nobody can apply a view
 * they are not allowed to see.
 */
export function SavedViewDialog({
  view,
  user,
  onClose,
}: {
  /** null to create. */
  view: SavedView | null;
  user: User;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState(view?.name ?? "");
  const [domains, setDomains] = useState((view?.domains ?? []).join(", "));
  const [pipelineIds, setPipelineIds] = useState<number[]>(
    view?.pipelineIds ?? [],
  );
  const [slaState, setSlaState] = useState<string>(view?.slaState ?? "");
  const [status, setStatus] = useState<"open" | "closed" | "all">(
    view?.status ?? "open",
  );
  const [isShared, setIsShared] = useState(view?.isShared ?? false);
  const [error, setError] = useState<string | null>(null);

  const pipelines = useQuery({
    queryKey: queryKeys.pipelines,
    queryFn: api.pipelines,
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const parsedDomains = domains
    .split(/[\s,;]+/)
    .map((domain) => domain.trim().toLowerCase().replace(/^@/, ""))
    .filter(Boolean);

  const payload = {
    name: name.trim(),
    isShared,
    domains: parsedDomains.length ? parsedDomains : null,
    pipelineIds: pipelineIds.length ? pipelineIds : null,
    slaState: (slaState || null) as SavedView["slaState"],
    status,
  };

  const save = useMutation({
    mutationFn: () =>
      view
        ? api.updateSavedView(view.id, payload)
        : api.createSavedView(payload),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.savedViews });
      onClose();
      navigate(`/v/saved-${saved.id}`);
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteSavedView(view!.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.savedViews });
      onClose();
      navigate("/v/open");
    },
  });

  // Mirrors the server's rule: a view with no filters is just "all tickets".
  const hasFilter =
    parsedDomains.length > 0 ||
    pipelineIds.length > 0 ||
    Boolean(slaState) ||
    status !== "open";

  const canSave = name.trim().length > 0 && hasFilter && !save.isPending;

  return (
    <Overlay
      title={view ? "Edit view" : "New view"}
      size="md"
      onClose={onClose}
      footer={
        <>
          {/* Left of the pair, because it is the one answer that is not about
              finishing what the dialog was opened for. */}
          {view ? (
            <Button
              variant="ghost"
              className="mr-auto"
              onClick={() => {
                if (window.confirm(`Delete the view “${view.name}”?`)) {
                  remove.mutate();
                }
              }}
            >
              Delete
            </Button>
          ) : null}

          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave}>
            {save.isPending ? "Saving…" : view ? "Save changes" : "Create view"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name" required>
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Installer.com, breaching"
            className={inputClass}
          />
        </Field>

        <Field
          label="Customer domains"
          hint="Comma separated. Leave blank for any."
        >
          <input
            value={domains}
            onChange={(event) => setDomains(event.target.value)}
            placeholder="installer.com, installer.co.uk"
            className={inputClass}
          />
        </Field>

        <Field label="Pipelines" hint="None selected means any.">
          <div className="flex flex-wrap gap-1">
            {(pipelines.data ?? []).map((pipeline) => {
              const active = pipelineIds.includes(pipeline.id);
              return (
                <button
                  key={pipeline.id}
                  type="button"
                  onClick={() =>
                    setPipelineIds((current) =>
                      active
                        ? current.filter((id) => id !== pipeline.id)
                        : [...current, pipeline.id],
                    )
                  }
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs transition-colors",
                    active
                      ? "bg-brand-600 text-white"
                      : "bg-surface-2 text-muted-on-tint hover:text-brand-600 dark:hover:text-brand-400",
                  )}
                >
                  {pipeline.name}
                </button>
              );
            })}
          </div>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Time to SLA">
            <select
              value={slaState}
              onChange={(event) => setSlaState(event.target.value)}
              className={inputClass}
            >
              <option value="">Any</option>
              <option value="breached">Over SLA</option>
              <option value="at_risk">Within 15 minutes</option>
              <option value="ok">Comfortably within</option>
            </select>
          </Field>

          <Field label="Tickets">
            <select
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as "open" | "closed" | "all")
              }
              className={inputClass}
            >
              <option value="open">Open only</option>
              <option value="closed">Closed only</option>
              <option value="all">Open and closed</option>
            </select>
          </Field>
        </div>

        {/* Sharing is a manager's call: a view everybody sees is a statement
            about how the desk works, not a personal shortcut. */}
        {user.role === "admin" ? (
          <label className="flex items-center gap-2 text-xs text-muted">
            <input
              type="checkbox"
              checked={isShared}
              onChange={(event) => setIsShared(event.target.checked)}
              className="accent-brand-600"
            />
            Visible to everyone
          </label>
        ) : null}

        {!hasFilter && name ? (
          <p className="text-xs text-amber-600">
            Add at least one filter — otherwise this is the same as “All open
            tickets”.
          </p>
        ) : null}
        {error ? <p className="text-xs text-red-600">{error}</p> : null}
      </div>
    </Overlay>
  );
}

const inputClass = cn(
  "w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
);

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline gap-1.5 text-xs text-muted">
        {label}
        {required ? <span className="text-brand-600">*</span> : null}
        {hint ? <span className="text-faint">{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}
